import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";

const loadStorefrontSession = () => import("./storefrontSession");

export function useMyTripsMenu() {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
    };
  }, [isMenuOpen]);

  // The session is checked on click rather than on mount, so page views don't
  // pay for the session query and the Apollo chunk.
  const openMenuOrSignIn = useCallback(async () => {
    if (isMenuOpen) {
      setIsMenuOpen(false);
      return;
    }

    if (isCheckingSession) return;

    setIsCheckingSession(true);
    try {
      const { hasStorefrontSession } = await loadStorefrontSession();

      if (!(await hasStorefrontSession())) {
        void router.push("/portal/login");
        return;
      }

      setIsMenuOpen(true);
    } finally {
      setIsCheckingSession(false);
    }
  }, [isCheckingSession, isMenuOpen, router]);

  const logOut = useCallback(async () => {
    try {
      const { endStorefrontSession, hasStorefrontSession } =
        await loadStorefrontSession();
      await endStorefrontSession();
      await hasStorefrontSession();
    } finally {
      setIsMenuOpen(false);
      await router.push("/portal/login");
    }
  }, [router]);

  const goToTrips = useCallback(() => {
    setIsMenuOpen(false);
    void router.push("/portal");
  }, [router]);

  return {
    goToTrips,
    isCheckingSession,
    isMenuOpen,
    logOut,
    openMenuOrSignIn,
    prefetchSession: loadStorefrontSession,
    wrapperRef,
  };
}
